<template>
    <div class="px-2 py-4 xl:px-10">
        <div class="max-w-8xl mx-auto w-full space-y-4">
            <!-- Page Header -->
            <div class="flex items-center justify-between">
                <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                    Materials Management
                </h1>
                <router-link
                    to="/materials/create"
                    class="inline-flex items-center px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                    <Plus class="w-4 h-4 mr-1" />
                    Add Material
                </router-link>
            </div>

            <!-- Search and Filters -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
                <div class="grid grid-cols-1 md:grid-cols-6 gap-4">
                    <!-- Search -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Search
                        </label>
                        <input
                            v-model="searchQuery"
                            @input="debouncedSearch"
                            type="text"
                            placeholder="Search by title, description, or type..."
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    <!-- Class Subject Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.class_subject_id"
                            :options="classSubjectOptions"
                            label="Class Subject"
                            placeholder="All Class Subjects"
                            :option-label="getClassSubjectLabel"
                            option-value="id"
                            @update:model-value="fetchMaterials"
                        />
                    </div>

                    <!-- Teacher Filter -->
                    <div v-if="!authStore.hasRole('teacher')">
                        <SearchableSelect
                            v-model="filters.teacher_id"
                            :options="teacherOptions"
                            label="Teacher"
                            placeholder="All Teachers"
                            :option-label="getTeacherName"
                            option-value="id"
                            @update:model-value="fetchMaterials"
                        />
                    </div>

                    <!-- Type Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.type"
                            :options="typeOptions"
                            label="Type"
                            placeholder="All Types"
                            option-label="label"
                            option-value="value"
                            @update:model-value="fetchMaterials"
                        />
                    </div>

                    <!-- Status Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.is_published"
                            :options="statusOptions"
                            label="Status"
                            placeholder="All Statuses"
                            option-label="label"
                            option-value="value"
                            @update:model-value="fetchMaterials"
                        />
                    </div>

                    <PageLengthSelector
                        v-model="materialStore.perPage"
                        @update:model-value="handlePerPageChange"
                    />
                </div>
                <div class="flex justify-between items-center mt-4">
                    <button
                        @click="resetFilters()"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    >
                        <RotateCcw class="w-4 h-4 mr-1" />
                        Reset Filters
                    </button>
                </div>
            </div>

            <!-- Loading State -->
            <div
                v-if="materialStore.loading"
                class="flex justify-center items-center py-12"
            >
                <div
                    class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
                ></div>
            </div>

            <!-- Error State -->
            <div
                v-if="materialStore.error"
                class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
            >
                <div class="flex">
                    <div class="flex-shrink-0">
                        <div class="h-5 w-5 text-red-400">⚠️</div>
                    </div>
                    <div class="ml-3">
                        <h3
                            class="text-sm font-medium text-red-800 dark:text-red-200"
                        >
                            Error
                        </h3>
                        <p class="mt-1 text-sm text-red-700 dark:text-red-300">
                            {{ materialStore.error }}
                        </p>
                        <button
                            @click="materialStore.clearError"
                            class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-500"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>

            <!-- Data Table -->
            <div
                v-if="!materialStore.loading && !materialStore.error"
                class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
            >
                <div
                    class="overflow-x-auto bg-gray-800 dark:bg-gray-900 shadow-md rounded-lg"
                >
                    <table
                        class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                        style="width: 100%"
                    >
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Title
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Type
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Class Subject
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Teacher
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    File Size
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Status
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody
                            class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                        >
                            <tr
                                v-for="material in materialStore.materials"
                                :key="material.id"
                                class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div
                                        class="text-sm font-medium text-gray-900 dark:text-white"
                                    >
                                        {{ material.title }}
                                    </div>
                                    <div
                                        v-if="material.description"
                                        class="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs"
                                        :title="material.description"
                                    >
                                        {{ material.description }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    <span
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                                        :class="getTypeClass(material.type)"
                                    >
                                        {{
                                            material.type_display ||
                                            material.type
                                        }}
                                    </span>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{
                                        material.class_subject?.class
                                            ?.grade_level || "-"
                                    }}
                                    -
                                    {{
                                        material.class_subject?.class?.name ||
                                        "-"
                                    }}
                                    -
                                    {{
                                        material.class_subject?.subject?.name ||
                                        "-"
                                    }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ material.teacher?.user?.name || "-" }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ material.file_size_formatted || "-" }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        v-if="material.is_published"
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                    >
                                        Published
                                    </span>
                                    <span
                                        v-else
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                    >
                                        Draft
                                    </span>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                                >
                                    <button
                                        @click="viewMaterial(material)"
                                        title="View Material"
                                        class="inline-flex items-center justify-center p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors mr-2"
                                    >
                                        <Eye class="w-4 h-4" />
                                    </button>
                                    <button
                                        @click="editMaterial(material)"
                                        title="Edit Material"
                                        class="inline-flex items-center justify-center p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors mr-2"
                                    >
                                        <Pencil class="w-4 h-4" />
                                    </button>
                                    <button
                                        @click="confirmDelete(material)"
                                        title="Delete Material"
                                        class="inline-flex items-center justify-center p-2 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    >
                                        <Trash2 class="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Empty State -->
                <div
                    v-if="materialStore.materials.length === 0"
                    class="text-center py-12"
                >
                    <BookOpen class="mx-auto h-12 w-12 text-gray-400" />
                    <h3
                        class="mt-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                        No materials found
                    </h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Get started by adding a new material.
                    </p>
                    <div class="mt-6">
                        <router-link
                            to="/materials/create"
                            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                            <Plus class="w-4 h-4 mr-1" />
                            Add Material
                        </router-link>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div
                v-if="
                    (materialStore.meta?.total || 0) >
                    (materialStore.meta?.per_page || 10)
                "
                class="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6"
            >
                <div class="flex-1 flex justify-between sm:hidden">
                    <button
                        @click="previousPage"
                        :disabled="materialStore.meta?.current_page === 1"
                        class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        Previous
                    </button>
                    <button
                        @click="nextPage"
                        :disabled="
                            materialStore.meta?.current_page ===
                            materialStore.meta?.last_page
                        "
                        class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        Next
                    </button>
                </div>
                <div
                    class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between"
                >
                    <div>
                        <p class="text-sm text-gray-700 dark:text-gray-300">
                            Showing
                            <span class="font-medium">{{
                                materialStore.meta?.from ?? 0
                            }}</span>
                            to
                            <span class="font-medium">{{
                                materialStore.meta?.to ?? 0
                            }}</span>
                            of
                            <span class="font-medium">{{
                                materialStore.meta?.total ?? 0
                            }}</span>
                            results
                        </p>
                    </div>
                    <div>
                        <nav
                            class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                            aria-label="Pagination"
                        >
                            <button
                                @click="previousPage"
                                :disabled="
                                    materialStore.meta?.current_page === 1
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                Previous
                            </button>
                            <button
                                v-for="page in visiblePages"
                                :key="page"
                                @click="
                                    typeof page === 'number'
                                        ? goToPage(page)
                                        : null
                                "
                                :class="[
                                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                    page === materialStore.meta?.current_page
                                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-blue-900 dark:text-blue-300'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300',
                                    typeof page === 'string'
                                        ? 'cursor-not-allowed'
                                        : 'cursor-pointer',
                                ]"
                                :disabled="typeof page === 'string'"
                            >
                                {{ page }}
                            </button>
                            <button
                                @click="nextPage"
                                :disabled="
                                    materialStore.meta?.current_page ===
                                    materialStore.meta?.last_page
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                Next
                            </button>
                        </nav>
                    </div>
                </div>
            </div>

            <!-- Delete Confirmation Modal -->
            <DeleteConfirmationModal
                v-if="showDeleteModal"
                :show="showDeleteModal"
                :title="deleteModalTitle"
                :message="deleteModalMessage"
                @confirm="handleDelete"
                @close="closeDeleteModal"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useMaterialStore } from "@/stores/materialStore";
import { useAuthStore } from "@/stores/authStore";
import SearchableSelect from "@/components/SearchableSelect.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import PageLengthSelector from "@/components/PageLengthSelector.vue";
import NotificationService from "@/utils/notifications";
import classSubjectApi from "@/api/classSubject";
import teacherApi from "@/api/teacher";
import type { Material } from "@/api/material";
import type { ClassSubject } from "@/api/classSubject";
import type { Teacher } from "@/api/teacher";
import { Plus, RotateCcw, Eye, Pencil, Trash2, BookOpen } from "@lucide/vue";

const router = useRouter();

const materialStore = useMaterialStore();
const authStore = useAuthStore();

// State
const searchQuery = ref("");
const filters = ref({
    class_subject_id: "",
    teacher_id: "",
    type: "",
    is_published: undefined as boolean | undefined,
});
const showDeleteModal = ref(false);
const materialToDelete = ref<Material | null>(null);
// Types for options with "Select All" option
type ClassSubjectOption =
    | ClassSubject
    | {
          id: "";
          class: { grade_level: string; name: string } | null;
          subject: { name: string } | null;
      };
type TeacherOption = Teacher | { id: ""; user: { name: string } | null };

const classSubjectOptions = ref<ClassSubjectOption[]>([]);
const teacherOptions = ref<TeacherOption[]>([]);

// Delete modal properties
const deleteModalTitle = ref("Confirm Delete");
const deleteModalMessage = ref(
    "Are you sure you want to delete this material? This action cannot be undone.",
);

// Computed
const visiblePages = computed(() => {
    const current = materialStore.meta?.current_page || 1;
    const last = materialStore.meta?.last_page || 1;
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
        let i = Math.max(2, current - delta);
        i <= Math.min(last - 1, current + delta);
        i++
    ) {
        range.push(i);
    }

    if (current - delta > 2) {
        rangeWithDots.push(1, "...");
    } else {
        rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (current + delta < last - 1) {
        rangeWithDots.push("...", last);
    } else if (last > 1) {
        rangeWithDots.push(last);
    }

    return rangeWithDots;
});

const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.class_subject_id !== "" ||
        filters.value.teacher_id !== "" ||
        filters.value.type !== "" ||
        filters.value.is_published !== undefined ||
        materialStore.page !== 1 ||
        materialStore.perPage !== 10
    );
});

// Options for Type filter
const typeOptions = computed(() => [
    { value: "", label: "All Types" },
    { value: "file", label: "File" },
    { value: "url", label: "URL" },
]);

// Options for Status filter
const statusOptions = computed(() => [
    { value: undefined, label: "All Statuses" },
    { value: true, label: "Published" },
    { value: false, label: "Unpublished" },
]);

// Methods
const handlePerPageChange = (value: number) => {
    materialStore.perPage = value;
    materialStore.page = 1;
    fetchMaterials();
};

const fetchMaterials = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
    };
    await materialStore.fetchMaterials(searchFilters);
};

const fetchOptions = async () => {
    try {
        // If logged in as teacher, filter class subjects by teacher_id
        const classSubjectFilters: any = { per_page: 1000 };
        if (authStore.user && authStore.hasRole("teacher")) {
            // Check if profile data is available and contains teacher info
            const profile = (authStore.user as any).profile;
            if (profile && profile.id) {
                classSubjectFilters.teacher_id = profile.id;
            }
        }

        // Only fetch teachers if not a teacher role
        const apiCalls = [classSubjectApi.getAll(classSubjectFilters)];
        if (!authStore.hasRole("teacher")) {
            apiCalls.push(teacherApi.getAll({ per_page: 1000 }));
        }

        const responses = await Promise.all(apiCalls);
        const [classSubjectsResponse, teachersResponse] = responses;

        // Add "Select All" option as the first element
        classSubjectOptions.value = [
            {
                id: "",
                class: { grade_level: "", name: "All Class Subjects" },
                subject: { name: "" },
            },
            ...(classSubjectsResponse.data?.items || []),
        ];

        if (!authStore.hasRole("teacher")) {
            teacherOptions.value = [
                { id: "", user: { name: "All Teachers" } },
                ...(teachersResponse.data?.items || []),
            ];
        }
    } catch (error) {
        console.error("Failed to fetch options:", error);
    }
};

const getClassSubjectLabel = (classSubject: ClassSubjectOption): string => {
    if (classSubject.id === "")
        return classSubject.class?.name || "All Class Subjects";
    return `${classSubject.class?.grade_level} - ${classSubject.class?.name} - ${classSubject.subject?.name}`;
};

const getTeacherName = (teacher: TeacherOption): string => {
    return teacher.user?.name || "All Teachers";
};

const debouncedSearch = useDebounce(fetchMaterials, 300);

const getTypeClass = (type: string) => {
    const typeClasses: Record<string, string> = {
        file: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
        url: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
    };
    return (
        typeClasses[type] ||
        "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
    );
};

const viewMaterial = (material: Material) => {
    // Handle view action based on material type
    switch (material.type) {
        case "file":
            // For uploaded files, try file_url first, then construct from file_path
            let fileUrl = material.file_url;
            // If no file_url but we have file_path, construct the URL
            if (!fileUrl && material.file_path) {
                // Remove leading slash if present and construct proper URL
                const cleanPath = material.file_path.startsWith("/")
                    ? material.file_path.substring(1)
                    : material.file_path;
                fileUrl = `/storage/${cleanPath}`;
            }
            console.info(fileUrl);

            if (fileUrl) {
                window.open(fileUrl, "_blank");
            } else {
                showNotification(
                    "No file available for this material",
                    "error",
                );
            }
            break;

        case "url":
            // For external URLs, use the url field
            if (material.url) {
                window.open(material.url, "_blank");
            } else {
                showNotification("No URL available for this material", "error");
            }
            break;

        default:
            // Fallback for unknown types
            if (material.file_url) {
                window.open(material.file_url, "_blank");
            } else if (material.file_path) {
                const cleanPath = material.file_path.startsWith("/")
                    ? material.file_path.substring(1)
                    : material.file_path;
                const constructedUrl = `/storage/${cleanPath}`;
                window.open(constructedUrl, "_blank");
            } else if (material.url) {
                window.open(material.url, "_blank");
            } else {
                showNotification(
                    "No content available for this material",
                    "error",
                );
            }
            break;
    }
};

const editMaterial = (material: Material) => {
    router.push(`/materials/${material.id}/edit`);
};

const confirmDelete = (material: Material) => {
    materialToDelete.value = material;
    deleteModalTitle.value = "Delete Material";
    deleteModalMessage.value = `Are you sure you want to delete "${material.title}"? This action cannot be undone.`;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    materialToDelete.value = null;
};

const handleDelete = async () => {
    try {
        if (materialToDelete.value) {
            await materialStore.deleteMaterial(materialToDelete.value.id);
            showNotification("Material deleted successfully", "success");
            closeDeleteModal();
        }
    } catch (error) {
        showNotification("Failed to delete material", "error");
    }
};

const previousPage = () => {
    if ((materialStore.meta?.current_page || 1) > 1) {
        materialStore.setPage((materialStore.meta?.current_page || 1) - 1);
        fetchMaterials();
    }
};

const nextPage = () => {
    if (
        (materialStore.meta?.current_page || 1) <
        (materialStore.meta?.last_page || 1)
    ) {
        materialStore.setPage((materialStore.meta?.current_page || 1) + 1);
        fetchMaterials();
    }
};

const goToPage = (page: number) => {
    materialStore.setPage(page);
    fetchMaterials();
};

const resetFilters = async () => {
    searchQuery.value = "";
    filters.value.class_subject_id = "";
    filters.value.teacher_id = "";
    filters.value.type = "";
    filters.value.is_published = undefined;
    await materialStore.resetFilters();
    fetchMaterials();
};

const showNotification = (
    message: string,
    type: "success" | "error" | "info" | "warning",
) => {
    switch (type) {
        case "success":
            NotificationService.success(message);
            break;
        case "error":
            NotificationService.error(message);
            break;
        case "info":
            NotificationService.info(message);
            break;
        case "warning":
            NotificationService.warning(message);
            break;
    }
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(() => {
    fetchMaterials();
    fetchOptions();
});
</script>
